import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Download,
  Copy,
  Trash2,
  Square,
  Link2,
  ZoomIn,
  ZoomOut,
  Move,
  Layers,
  BedDouble,
  GripVertical,
  Maximize2,
  Minimize2,
} from "lucide-react";

/* ----------------------------- UI Primitives ----------------------------- */

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
      <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70" />
    </div>
    <div className="relative">{children}</div>
  </div>
);

const Glass = React.forwardRef(({ children, className = "" }, ref) => (
  <div
    ref={ref}
    className={[
      "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl",
      "shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
      className,
    ].join(" ")}
  >
    {children}
  </div>
));

const PrimaryButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "px-4 py-2.5 rounded-2xl font-semibold text-sm",
      "bg-white text-black hover:bg-white/90",
      "shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)]",
      "transition disabled:opacity-50 disabled:hover:bg-white",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "px-4 py-2.5 rounded-2xl font-semibold text-sm",
      "border border-white/12 bg-white/5 hover:bg-white/8",
      "text-white/85 transition disabled:opacity-50",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const IconButton = ({ title, onClick, disabled, children, active = false }) => (
  <button
    title={title}
    onClick={onClick}
    disabled={disabled}
    className={[
      "h-10 w-10 rounded-2xl border border-white/12 flex items-center justify-center text-white/85 transition",
      active ? "bg-white/15 border-white/25" : "bg-white/5 hover:bg-white/8",
      "disabled:opacity-50",
    ].join(" ")}
  >
    {children}
  </button>
);

const Pill = ({ children, tone = "neutral" }) => {
  const cls =
    tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : tone === "sky"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "purple"
      ? "border-purple-400/20 bg-purple-400/10 text-purple-100"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide",
        cls,
      ].join(" ")}
    >
      {children}
    </span>
  );
};

/* ----------------------------- Helpers ----------------------------- */

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const snap = (n, grid) => Math.round(n / grid) * grid;

const toBoardPoint = (clientX, clientY, rect, viewport) => ({
  x: (clientX - rect.left - viewport.x) / viewport.scale,
  y: (clientY - rect.top - viewport.y) / viewport.scale,
});

/* ----------------------------- Node Library ----------------------------- */

const NODE_LIBRARY = [
  { type: "trigger",   title: "Trigger",   tone: "emerald", subtitle: "Starts the flow" },
  { type: "action",    title: "Action",    tone: "sky",     subtitle: "Transforms / calls API" },
  { type: "condition", title: "Condition", tone: "purple",  subtitle: "Branch logic" },
  { type: "output",    title: "Output",    tone: "neutral", subtitle: "Final payload" },
];

const DEFAULT_NODE_SIZE = { w: 280, h: 140 };
const GRID = 20;

function safePreview(data) {
  try {
    const s = JSON.stringify(data ?? {}, null, 0);
    return s.length > 120 ? s.slice(0, 120) + "…" : s;
  } catch { return "{}"; }
}

function getHandlePoint(node, handle) {
  const { x, y } = node.position;
  const { w, h } = node.size || DEFAULT_NODE_SIZE;
  const midY = y + h / 2;
  return handle === "in" ? { x: x - 10, y: midY } : { x: x + w + 10, y: midY };
}

function makeBezier(a, b) {
  const dx = Math.max(80, Math.abs(b.x - a.x) * 0.5);
  return `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`;
}

/* ----------------------------- Main Component ----------------------------- */

export default function WhiteboardFlow() {
  const boardRef = useRef(null);

  const [viewport, setViewport] = useState({ x: 120, y: 80, scale: 1 });

  const [nodes, setNodes] = useState(() => {
    const aId = uid();
    const bId = uid();
    return [
      {
        id: aId,
        type: "trigger",
        label: "Webhook Trigger",
        position: { x: 140, y: 120 },
        size: { ...DEFAULT_NODE_SIZE },
        data: { description: "Incoming request" },
      },
      {
        id: bId,
        type: "action",
        label: "Analyze Resume",
        position: { x: 520, y: 130 },
        size: { ...DEFAULT_NODE_SIZE },
        data: { model: "grok", temperature: 0.2 },
      },
    ];
  });

  // Start with no edges — user draws them
  const [edges, setEdges] = useState([]);

  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const [mode, setMode] = useState("select"); // "select" | "pan"
  const [snapToGrid, setSnapToGrid] = useState(true);

  // All pointer interaction state lives here — no useState for perf
  const dragRef = useRef(null);  // { type:'node'|'pan', nodeId?, grabOffset?, startViewport?, pointerId }
  const linkRef = useRef(null);  // { fromNodeId, fromHandle, pointerId } | null

  const [linkPreview, setLinkPreview] = useState(null); // { from, to } in board coords
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const boardRect = () => boardRef.current?.getBoundingClientRect() ?? null;

  /* ----------------------------- Fullscreen ----------------------------- */

  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  // Also close fullscreen on Escape (added alongside existing keyboard handler)
  // Handled inside the existing keyboard useEffect via the Escape branch below.

  /* ----------------------------- Add Node ----------------------------- */

  const addNode = (type) => {
    const tpl = NODE_LIBRARY.find((t) => t.type === type) || NODE_LIBRARY[1];
    const id = uid();
    const rect = boardRect();
    let center = { x: 280, y: 200 };
    if (rect) {
      const sc = { x: rect.left + rect.width * 0.5, y: rect.top + rect.height * 0.45 };
      center = toBoardPoint(sc.x, sc.y, rect, viewport);
    }
    const node = {
      id,
      type: tpl.type,
      label: tpl.title,
      position: {
        x: snapToGrid ? snap(center.x, GRID) : center.x,
        y: snapToGrid ? snap(center.y, GRID) : center.y,
      },
      size: { ...DEFAULT_NODE_SIZE },
      data: { meta: { createdAt: Date.now() } },
    };
    setNodes((prev) => [...prev, node]);
    setSelectedNodeId(id);
  };

  /* ----------------------------- Node Ops ----------------------------- */

  const deleteSelected = () => {
    if (!selectedNodeId) return;
    const id = selectedNodeId;
    setNodes((prev) => prev.filter((n) => n.id !== id));
    setEdges((prev) => prev.filter((e) => e.source !== id && e.target !== id));
    setSelectedNodeId(null);
  };

  const duplicateSelected = () => {
    if (!selectedNode) return;
    const copy = {
      ...selectedNode,
      id: uid(),
      label: `${selectedNode.label} Copy`,
      position: { x: selectedNode.position.x + 30, y: selectedNode.position.y + 30 },
      data: JSON.parse(JSON.stringify(selectedNode.data || {})),
    };
    setNodes((prev) => [...prev, copy]);
    setSelectedNodeId(copy.id);
  };

  const updateSelected = (patch) => {
    if (!selectedNodeId) return;
    setNodes((prev) =>
      prev.map((n) => (n.id === selectedNodeId ? { ...n, ...patch } : n))
    );
  };

  /* ----------------------------- Export ----------------------------- */

  const exportJSON = () => {
    const payload = {
      version: "1.0",
      exportedAt: new Date().toISOString(),
      viewport: { ...viewport },
      settings: { snapToGrid, gridSize: GRID },
      nodes: nodes.map(({ id, type, label, position, size, data }) => ({ id, type, label, position, size, data: data || {} })),
      edges: edges.map(({ id, source, target, sourceHandle, targetHandle }) => ({ id, source, target, sourceHandle, targetHandle })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "whiteboard-flow.json"; a.click();
    URL.revokeObjectURL(url);
    setToast("Exported JSON ✅");
  };

  const copyJSON = async () => {
    const payload = {
      version: "1.0",
      exportedAt: new Date().toISOString(),
      viewport: { ...viewport },
      settings: { snapToGrid, gridSize: GRID },
      nodes: nodes.map(({ id, type, label, position, size, data }) => ({ id, type, label, position, size, data: data || {} })),
      edges: edges.map(({ id, source, target, sourceHandle, targetHandle }) => ({ id, source, target, sourceHandle, targetHandle })),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setToast("Copied JSON ✅");
    } catch {
      setToast("Clipboard blocked — use Export");
    }
  };

  /* ----------------------------- Keyboard ----------------------------- */

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const typing = tag === "input" || tag === "textarea" || tag === "select";
      if (!typing && (e.key === "Delete" || e.key === "Backspace")) deleteSelected();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") { e.preventDefault(); duplicateSelected(); }
      if (!typing && e.key === " " && mode !== "connect") setMode("pan");
      if (e.key === "Escape") { linkRef.current = null; setLinkPreview(null); setMode("select"); setIsFullscreen(false); }
    };
    const onKeyUp = (e) => { if (e.key === " " && mode === "pan") setMode("select"); };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedNodeId, selectedNode]);

  /* ----------------------------- Zoom ----------------------------- */

  const zoomTo = (nextScale, originClient) => {
    const rect = boardRect();
    if (!rect) return;
    const scale = clamp(nextScale, 0.25, 3);
    const ox = originClient?.x ?? rect.left + rect.width / 2;
    const oy = originClient?.y ?? rect.top + rect.height / 2;
    const before = toBoardPoint(ox, oy, rect, viewport);
    const next = { ...viewport, scale };
    next.x += ox - rect.left - before.x * scale - next.x;
    next.y += oy - rect.top - before.y * scale - next.y;
    setViewport(next);
  };

  const onWheel = (e) => {
    e.preventDefault();
    zoomTo(viewport.scale * (e.deltaY < 0 ? 1.07 : 0.93), { x: e.clientX, y: e.clientY });
  };

  /* ----------------------------- completeConnect (defined early — used in onBoardPointerUp) ---- */

  const completeConnect = (e, targetNodeId, targetHandle) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!linkRef.current) return;

    const { fromNodeId, fromHandle, pointerId } = linkRef.current;

    const releaseAndReset = () => {
      try { boardRef.current?.releasePointerCapture?.(pointerId); } catch {}
      linkRef.current = null;
      setLinkPreview(null);
      setMode("select");
    };

    // Validate direction: out → in
    const ok =
      (fromHandle === "out" && targetHandle === "in") ||
      (fromHandle === "in" && targetHandle === "out");

    if (!ok) { setToast("Connect output → input"); releaseAndReset(); return; }

    const source = fromHandle === "out" ? fromNodeId : targetNodeId;
    const target = fromHandle === "out" ? targetNodeId : fromNodeId;

    if (source === target) { setToast("Can't connect to itself"); releaseAndReset(); return; }

    const dup = edges.some(
      (ed) => ed.source === source && ed.target === target &&
               ed.sourceHandle === "out" && ed.targetHandle === "in"
    );
    if (dup) { setToast("Edge already exists"); releaseAndReset(); return; }

    setEdges((prev) => [
      ...prev,
      { id: uid(), source, target, sourceHandle: "out", targetHandle: "in" },
    ]);
    setToast("Connection created ✅");
    releaseAndReset();
  };

  /* ----------------------------- Hit-test IN handle via elementsFromPoint --------------- */

  const getInHandleTarget = (clientX, clientY) => {
    const els = document.elementsFromPoint(clientX, clientY);
    for (const el of els) {
      const nid = el.dataset?.nodeId;
      const h   = el.dataset?.handle;
      if (nid && h) return { nodeId: nid, handle: h };
    }
    return null;
  };

  /* ----------------------------- Board Pointer ----------------------------- */

  const onBoardPointerDown = (e) => {
    if (e.button !== 0) return;

    // Cancel connect on board click
    if (linkRef.current) {
      try { boardRef.current?.releasePointerCapture?.(linkRef.current.pointerId); } catch {}
      linkRef.current = null;
      setLinkPreview(null);
      setMode("select");
      return;
    }

    // Deselect on empty board
    if (e.target === boardRef.current) setSelectedNodeId(null);

    if (mode === "pan") {
      dragRef.current = {
        type: "pan",
        startClient: { x: e.clientX, y: e.clientY },
        startViewport: { ...viewport },
        pointerId: e.pointerId,
      };
      try { boardRef.current?.setPointerCapture?.(e.pointerId); } catch {}
    }
  };

  const onBoardPointerMove = (e) => {
    const rect = boardRect();
    if (!rect) return;

    // Pan
    if (dragRef.current?.type === "pan") {
      const dx = e.clientX - dragRef.current.startClient.x;
      const dy = e.clientY - dragRef.current.startClient.y;
      setViewport({
        ...dragRef.current.startViewport,
        x: dragRef.current.startViewport.x + dx,
        y: dragRef.current.startViewport.y + dy,
      });
      return;
    }

    // Node drag
    if (dragRef.current?.type === "node") {
      const p = toBoardPoint(e.clientX, e.clientY, rect, viewport);
      let nx = p.x - dragRef.current.grabOffset.x;
      let ny = p.y - dragRef.current.grabOffset.y;
      if (snapToGrid) { nx = snap(nx, GRID); ny = snap(ny, GRID); }
      const nid = dragRef.current.nodeId;
      setNodes((prev) =>
        prev.map((n) => n.id === nid ? { ...n, position: { x: nx, y: ny } } : n)
      );
      return;
    }

    // Link preview
    if (linkRef.current) {
      const to = toBoardPoint(e.clientX, e.clientY, rect, viewport);
      const fromNode = nodes.find((n) => n.id === linkRef.current.fromNodeId);
      if (!fromNode) return;
      setLinkPreview({ from: getHandlePoint(fromNode, linkRef.current.fromHandle), to });
    }
  };

  const onBoardPointerUp = (e) => {
    // Release pan
    if (dragRef.current?.type === "pan" && dragRef.current.pointerId === e.pointerId) {
      dragRef.current = null;
      try { boardRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
      return;
    }

    // Release node drag
    if (dragRef.current?.type === "node" && dragRef.current.pointerId === e.pointerId) {
      dragRef.current = null;
      try { boardRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
      return;
    }

    // Complete link — hit-test via elementsFromPoint since pointer capture swallows events
    if (linkRef.current?.pointerId === e.pointerId) {
      const target = getInHandleTarget(e.clientX, e.clientY);
      if (target) {
        completeConnect(e, target.nodeId, target.handle);
      } else {
        // Dropped on empty — cancel
        try { boardRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
        linkRef.current = null;
        setLinkPreview(null);
        setMode("select");
      }
    }
  };

  /* ----------------------------- Node Drag ----------------------------- */

  const onNodePointerDown = (e, nodeId) => {
    if (e.button !== 0) return;
    if (linkRef.current) return; // don't start drag while connecting
    e.stopPropagation();

    const rect = boardRect();
    if (!rect) return;

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    setSelectedNodeId(nodeId);

    const p = toBoardPoint(e.clientX, e.clientY, rect, viewport);
    dragRef.current = {
      type: "node",
      nodeId,
      grabOffset: { x: p.x - node.position.x, y: p.y - node.position.y },
      pointerId: e.pointerId,
    };
    try { boardRef.current?.setPointerCapture?.(e.pointerId); } catch {}
  };

  /* ----------------------------- FK Connect ----------------------------- */

  const beginConnect = (e, nodeId, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = boardRect();
    if (!rect) return;

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const pointerId = e.pointerId;
    linkRef.current = { fromNodeId: nodeId, fromHandle: handle, pointerId };

    // Capture so move events always reach board even outside dot
    try { boardRef.current?.setPointerCapture?.(pointerId); } catch {}

    const from = getHandlePoint(node, handle);
    const to   = toBoardPoint(e.clientX, e.clientY, rect, viewport);
    setLinkPreview({ from, to });
  };

  /* ----------------------------- Rendered Edges ----------------------------- */

  const renderedEdges = useMemo(() => {
    return edges
      .map((e) => {
        const s = nodes.find((n) => n.id === e.source);
        const t = nodes.find((n) => n.id === e.target);
        if (!s || !t) return null;
        return { ...e, path: makeBezier(getHandlePoint(s, "out"), getHandlePoint(t, "in")) };
      })
      .filter(Boolean);
  }, [edges, nodes]);

  /* ----------------------------- Render ----------------------------- */

  return (
    <AppBackground>
      <div className="mx-auto max-w-[1400px] px-6 py-8 mt-20">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Left Panel ── */}
          <Glass className="lg:w-[360px] p-5 flex flex-col gap-5">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold tracking-tight">Flow Whiteboard</div>
                <div className="text-white/60 text-sm mt-1">n8n-style drag, connect, export JSON</div>
              </div>
              <Pill tone={linkRef.current ? "sky" : "neutral"}>
                <Link2 className="h-3.5 w-3.5" />
                {linkRef.current ? "Connecting" : "Ready"}
              </Pill>
            </div>

            {/* Node palette */}
            <div className="grid grid-cols-2 gap-3">
              {NODE_LIBRARY.map((t) => (
                <button
                  key={t.type}
                  onClick={() => addNode(t.type)}
                  className="rounded-2xl border border-white/12 bg-white/5 hover:bg-white/8 p-3 text-left transition"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">{t.title}</div>
                    <Pill tone={t.tone}>{t.type}</Pill>
                  </div>
                  <div className="text-[12px] text-white/60 mt-1">{t.subtitle}</div>
                </button>
              ))}
            </div>

            {/* Controls */}
            <div className="rounded-2xl border border-white/12 bg-white/5 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4 text-white/70" />
                  Controls
                </div>
                <Pill tone="emerald">
                  <Move className="h-3.5 w-3.5" />
                  Space = Pan
                </Pill>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <IconButton title="Zoom In"  onClick={() => zoomTo(viewport.scale * 1.12)}>
                  <ZoomIn className="h-4 w-4" />
                </IconButton>
                <IconButton title="Zoom Out" onClick={() => zoomTo(viewport.scale * 0.9)}>
                  <ZoomOut className="h-4 w-4" />
                </IconButton>
                <IconButton title="Reset view" onClick={() => setViewport({ x: 120, y: 80, scale: 1 })}>
                  <Square className="h-4 w-4" />
                </IconButton>
                <div className="ml-auto">
                  <label className="flex items-center gap-2 text-sm text-white/70 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={snapToGrid}
                      onChange={(ev) => setSnapToGrid(ev.target.checked)}
                      className="accent-white"
                    />
                    Snap
                  </label>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <GhostButton onClick={copyJSON} className="flex-1">
                  <span className="inline-flex items-center gap-2">
                    <Copy className="h-4 w-4" />
                    Copy
                  </span>
                </GhostButton>
                <PrimaryButton onClick={exportJSON} className="flex-1">
                  <span className="inline-flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export
                  </span>
                </PrimaryButton>
              </div>

              <div className="mt-3 text-[12px] text-white/55 leading-relaxed">
                Drag <span className="text-emerald-200">green dot</span> → drop on{" "}
                <span className="text-sky-200">blue dot</span> to connect.{" "}
                <span className="text-white/80">Del</span> = delete &nbsp;
                <span className="text-white/80">⌘D</span> = duplicate
              </div>
            </div>

            {/* Inspector */}
            <div className="rounded-2xl border border-white/12 bg-white/5 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Inspector</div>
                <div className="text-[12px] text-white/60">{selectedNode ? "Selected" : "None"}</div>
              </div>

              {!selectedNode ? (
                <div className="mt-3 text-sm text-white/60">Click a node to edit label / data.</div>
              ) : (
                <div className="mt-3 space-y-3">
                  <div>
                    <div className="text-[12px] text-white/60 mb-1">Label</div>
                    <input
                      value={selectedNode.label}
                      onChange={(ev) => updateSelected({ label: ev.target.value })}
                      className="w-full rounded-2xl border border-white/12 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
                    />
                  </div>
                  <div>
                    <div className="text-[12px] text-white/60 mb-1">Data (JSON)</div>
                    <textarea
                      value={JSON.stringify(selectedNode.data || {}, null, 2)}
                      onChange={(ev) => {
                        try { updateSelected({ data: JSON.parse(ev.target.value || "{}") }); }
                        catch { /* ignore invalid JSON while typing */ }
                      }}
                      rows={5}
                      className="w-full rounded-2xl border border-white/12 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25 font-mono resize-none"
                    />
                  </div>
                  <div className="flex gap-2">
                    <GhostButton onClick={duplicateSelected} className="flex-1">
                      <span className="inline-flex items-center gap-2">
                        <BedDouble className="h-4 w-4" />
                        Duplicate
                      </span>
                    </GhostButton>
                    <GhostButton onClick={deleteSelected} className="flex-1 border-red-500/20">
                      <span className="inline-flex items-center gap-2 text-red-200">
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </span>
                    </GhostButton>
                  </div>
                </div>
              )}
            </div>
          </Glass>

          {/* ── Board ── */}
          <Glass className={isFullscreen
            ? "fixed inset-0 z-50 rounded-none p-4 flex flex-col"
            : "flex-1 p-4"
          }>
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 px-2 pb-3">
              <div className="flex items-center gap-2">
                <Pill tone="emerald">
                  <Plus className="h-3.5 w-3.5" />
                  Nodes: {nodes.length}
                </Pill>
                <Pill tone="sky">
                  <Link2 className="h-3.5 w-3.5" />
                  Edges: {edges.length}
                </Pill>
                <Pill tone="neutral">
                  Zoom: {(viewport.scale * 100).toFixed(0)}%
                </Pill>
              </div>

              <div className="flex items-center gap-2">
                <GhostButton
                  onClick={() => setMode("select")}
                  className={mode === "select" ? "bg-white/12 border-white/20" : ""}
                >
                  Select
                </GhostButton>
                <GhostButton
                  onClick={() => setMode("pan")}
                  className={mode === "pan" ? "bg-white/12 border-white/20" : ""}
                >
                  Pan
                </GhostButton>
                <IconButton
                  title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen board"}
                  onClick={toggleFullscreen}
                  active={isFullscreen}
                >
                  {isFullscreen
                    ? <Minimize2 className="h-4 w-4" />
                    : <Maximize2 className="h-4 w-4" />
                  }
                </IconButton>
              </div>
            </div>

            {/* Canvas */}
            <div
              ref={boardRef}
              onWheel={onWheel}
              onPointerDown={onBoardPointerDown}
              onPointerMove={onBoardPointerMove}
              onPointerUp={onBoardPointerUp}
              className={[
                "relative w-full overflow-hidden rounded-3xl",
                "border border-white/10 bg-black/35",
                isFullscreen ? "flex-1" : "h-[760px]",
                mode === "pan" ? "cursor-grab active:cursor-grabbing" : "cursor-default",
              ].join(" ")}
              style={{ touchAction: "none" }}
            >
              {/* Grid */}
              <div
                className="pointer-events-none absolute inset-0 opacity-70"
                style={{
                  backgroundImage: `radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)`,
                  backgroundSize: `26px 26px, 92px 92px`,
                }}
              />

              {/* Transform layer */}
              <div
                className="absolute inset-0"
                style={{
                  transform: `translate(${viewport.x}px,${viewport.y}px) scale(${viewport.scale})`,
                  transformOrigin: "0 0",
                }}
              >
                {/* Edges */}
                <svg className="absolute inset-0 overflow-visible pointer-events-none">
                  <defs>
                    <linearGradient id="edgeGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%"   stopColor="rgba(16,185,129,0.70)" />
                      <stop offset="100%" stopColor="rgba(56,189,248,0.70)" />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2.2" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>

                  {renderedEdges.map((e) => (
                    <path key={e.id} d={e.path} fill="none"
                      stroke="url(#edgeGrad)" strokeWidth="3"
                      opacity="0.95" filter="url(#glow)"
                    />
                  ))}

                  {linkPreview && (
                    <path
                      d={makeBezier(linkPreview.from, linkPreview.to)}
                      fill="none"
                      stroke="rgba(255,255,255,0.50)"
                      strokeWidth="2.5"
                      strokeDasharray="7 5"
                    />
                  )}
                </svg>

                {/* Nodes */}
                {nodes.map((n) => (
                  <NodeCard
                    key={n.id}
                    node={n}
                    selected={n.id === selectedNodeId}
                    onSelect={() => setSelectedNodeId(n.id)}
                    onPointerDown={(e) => onNodePointerDown(e, n.id)}
                    onBeginConnect={(e) => beginConnect(e, n.id, "out")}
                  />
                ))}
              </div>

              {/* Toast */}
              {toast && (
                <div className="absolute left-4 bottom-4 pointer-events-none">
                  <div className="rounded-2xl border border-white/12 bg-black/70 backdrop-blur-xl px-4 py-2 text-sm text-white/90">
                    {toast}
                  </div>
                </div>
              )}
            </div>
          </Glass>
        </div>
      </div>
    </AppBackground>
  );
}

/* ----------------------------- NodeCard ----------------------------- */

function NodeCard({ node, selected, onSelect, onPointerDown, onBeginConnect }) {
  const tpl = NODE_LIBRARY.find((t) => t.type === node.type) || NODE_LIBRARY[1];

  const accent =
    tpl.tone === "emerald" ? "from-emerald-400/20 via-emerald-400/8 to-transparent"
    : tpl.tone === "sky"   ? "from-sky-400/20 via-sky-400/8 to-transparent"
    : tpl.tone === "purple"? "from-purple-400/20 via-purple-400/8 to-transparent"
                           : "from-white/16 via-white/6 to-transparent";

  return (
    <div
      onPointerDown={(e) => {
        // Only start node drag — not from handle areas
        onPointerDown(e);
        onSelect();
      }}
      className={[
        "absolute select-none",
        "rounded-3xl border bg-white/[0.05] backdrop-blur-2xl",
        selected
          ? "border-white/20 ring-2 ring-white/30 shadow-[0_24px_70px_-30px_rgba(255,255,255,0.30)]"
          : "border-white/12 shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
      ].join(" ")}
      style={{
        left: node.position.x,
        top: node.position.y,
        width: node.size?.w || 280,
        // height: node.size?.h || 140,
      }}
    >
      {/* Accent wash */}
      <div className={["pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br", accent].join(" ")} />

      {/* Header */}
      <div className="relative flex items-start justify-between p-4 cursor-grab active:cursor-grabbing">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold truncate">{node.label}</div>
            <Pill tone={tpl.tone}>{tpl.type}</Pill>
          </div>
          <div className="text-[12px] text-white/60 mt-1 truncate">{tpl.subtitle}</div>
        </div>
        <GripVertical className="h-4 w-4 text-white/30 flex-shrink-0 mt-0.5" />
      </div>

      {/* Body */}
      <div className="relative px-4 pb-4">
        <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
          <div className="text-[12px] text-white/55 mb-1">Data preview</div>
          <div className="text-[12px] font-mono text-white/80 line-clamp-2">{safePreview(node.data)}</div>
        </div>
      </div>

      {/* IN handle — left side. data-* attrs for elementsFromPoint hit-test */}
      <div
        className="absolute left-[-12px] top-1/2 -translate-y-1/2 z-10 cursor-crosshair p-1"
        data-node-id={node.id}
        data-handle="in"
        title="Input"
      >
        <HandleDot tone="in" nodeId={node.id} handle="in" />
      </div>

      {/* OUT handle — right side */}
      <div
        className="absolute right-[-12px] top-1/2 -translate-y-1/2 z-10 cursor-crosshair p-1"
        onPointerDown={(e) => {
          e.stopPropagation(); // don't trigger node drag
          onBeginConnect(e);
        }}
        title="Output — drag to connect"
      >
        <HandleDot tone="out" nodeId={node.id} handle="out" />
      </div>
    </div>
  );
}

/* ----------------------------- HandleDot ----------------------------- */

function HandleDot({ tone, nodeId, handle }) {
  const cls =
    tone === "out"
      ? "bg-emerald-300/90 shadow-[0_0_0_6px_rgba(16,185,129,0.15)] hover:shadow-[0_0_0_8px_rgba(16,185,129,0.25)]"
      : "bg-sky-300/90 shadow-[0_0_0_6px_rgba(56,189,248,0.15)] hover:shadow-[0_0_0_8px_rgba(56,189,248,0.25)]";

  return (
    <div
      data-node-id={nodeId}
      data-handle={handle}
      className={[
        "h-5 w-5 rounded-full border border-white/25 backdrop-blur-xl transition-shadow",
        cls,
      ].join(" ")}
    />
  );
}
